/**
 * Getting a real catalog into the system, without finding out afterwards.
 *
 * Nobody types a parts catalog into a form. It arrives as a vendor's
 * spreadsheet or somebody else's export, and the only useful thing this panel
 * can do is say precisely what is wrong with that file BEFORE any of it lands.
 * So the primary action is CHECK THIS FILE, not import. Import is the second
 * button and it stays disabled until a check comes back clean.
 *
 * Three things this panel is not allowed to do, each of them a way a catalog
 * import has quietly gone wrong somewhere:
 *
 *   A refusal must not look like a success. The server validates the whole
 *   bundle and writes none of it if anything fails, so `ok: false` is rendered
 *   as NOTHING WAS WRITTEN with the reasons, never as a warning on a result.
 *
 *   There is no such thing as a partial import. No copy here says "some rows
 *   landed", because the server cannot produce that outcome and inventing the
 *   possibility sends an operator looking for a half-written catalog.
 *
 *   An overwrite is a warning, not a footnote. Replacing `motor-x` silently
 *   discards a count somebody walked a shelf to correct, so the ids and the
 *   number are stated above the counts, before the operator commits.
 *
 * The problem list is grouped by kind with the row number in front, because a
 * 500-row CSV with 12 problems has to be a to-do list, not a paragraph.
 */

import { useRef, useState, type ReactNode } from 'react';

import {
  getImportTemplate,
  IMPORT_KINDS,
  type ImportKind,
  type ImportProblem,
  type ImportReport,
  type ImportRequest,
} from '@/api/supply';
import { supplyActions, useSupplyImport } from '@/stores/supplyStore';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const WHITE = '#f4f4f5';
const MUTED = 'rgba(244,244,245,0.5)';

/**
 * Problems shown per kind before the list is cut.
 *
 * A file broken in the same way on every row produces thousands of problems and
 * rendering all of them turns the panel into a wall. Whatever is cut is counted
 * and said out loud — a silently shortened list is how an operator fixes eleven
 * of twelve problems and cannot work out why the check still fails.
 */
const PROBLEMS_PER_KIND = 50;

/** Ids listed per kind in the overwrite warning before the rest are counted. */
const OVERWRITE_IDS_SHOWN = 40;

function download(name: string, mime: string, body: string): void {
  const blob = new Blob([body], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function total(counts: Record<string, number>): number {
  return Object.values(counts).reduce((sum, n) => sum + n, 0);
}

function nonEmpty(counts: Record<string, number>): [string, number][] {
  return Object.entries(counts).filter(([, n]) => n > 0);
}

type Built =
  | { request: Omit<ImportRequest, 'ifMatch' | 'dryRun'>; error: null }
  | { request: null; error: string };

/**
 * What the operator pasted, as the request the server takes.
 *
 * A JSON object is a bundle of entity lists; a bare JSON list is the rows for
 * the kind selected above it; anything else is a CSV of that kind. A file that
 * will not parse is refused here with the parser's own reason rather than sent
 * off to come back as a generic 422 — the operator gets the same answer either
 * way, and getting it without a round trip is not a worse answer.
 */
function buildRequest(kind: ImportKind, text: string): Built {
  const trimmed = text.trim();
  if (!trimmed) {
    return { request: null, error: 'Nothing to check yet. Paste a CSV, or drop a .csv or .json file.' };
  }

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch (err) {
      const why = err instanceof Error ? err.message : 'unreadable';
      return { request: null, error: `That is not valid JSON, so nothing was checked: ${why}` };
    }
    if (Array.isArray(parsed)) {
      return { request: { bundle: { [kind]: parsed } }, error: null };
    }
    if (parsed !== null && typeof parsed === 'object') {
      return { request: { bundle: parsed as Record<string, unknown> }, error: null };
    }
    return {
      request: null,
      error: 'A JSON import must be an object of entity lists, or a list of rows for the kind selected above.',
    };
  }

  return { request: { csv: text, kind }, error: null };
}

function groupByKind(problems: ImportProblem[]): { kind: string; rows: ImportProblem[] }[] {
  // Server order is kept: problems arrive in dependency order, so a missing
  // part is listed before the platform that could not find it, which is the
  // order they have to be fixed in.
  const groups: { kind: string; rows: ImportProblem[] }[] = [];
  for (const problem of problems) {
    const group = groups.find((candidate) => candidate.kind === problem.kind);
    if (group) group.rows.push(problem);
    else groups.push({ kind: problem.kind, rows: [problem] });
  }
  return groups;
}

function Note({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <p
      className="rounded-[11px] border px-3 py-2 text-[11.5px]"
      role="status"
      style={{
        color: tone,
        borderColor: `color-mix(in oklch, ${tone} 45%, transparent)`,
        background: `color-mix(in oklch, ${tone} 10%, transparent)`,
      }}
    >
      {children}
    </p>
  );
}

function CountRow({ label, counts }: { label: string; counts: Record<string, number> }) {
  const entries = nonEmpty(counts);
  if (entries.length === 0) {
    return (
      <p className="text-[11.5px]" style={{ color: MUTED }}>
        {label}: no rows at all. That file carries nothing.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1.5">
      <span className="label-mono text-[9px]" style={{ color: MUTED, letterSpacing: '0.14em' }}>
        {label}
      </span>
      {entries.map(([kind, n]) => (
        <span key={kind} className="flex items-baseline gap-1.5" data-testid={`import-count-${kind}`}>
          <span className="tnum text-[19px] font-semibold leading-none" style={{ color: WHITE }}>
            {n}
          </span>
          <span className="label-mono text-[9px]" style={{ color: MUTED }}>
            {kind.toUpperCase()}
          </span>
        </span>
      ))}
    </div>
  );
}

/**
 * Which ids this import replaces, stated before it can be run.
 *
 * The failure being prevented is specific: an import that quietly overwrites a
 * hand-corrected count with the number from a stale spreadsheet, leaving no
 * trace that a physical count was thrown away.
 */
function OverwriteWarning({ overwrites }: { overwrites: Record<string, string[]> }) {
  const entries = Object.entries(overwrites).filter(([, ids]) => ids.length > 0);
  if (entries.length === 0) return null;
  const count = entries.reduce((sum, [, ids]) => sum + ids.length, 0);

  return (
    <div
      data-testid="import-overwrites"
      className="rounded-[13px] border px-[13px] py-[11px]"
      style={{
        borderColor: `color-mix(in oklch, ${WARN} 55%, transparent)`,
        background: `color-mix(in oklch, ${WARN} 12%, transparent)`,
      }}
    >
      <p className="label-mono text-[10px] font-semibold" style={{ color: WARN, letterSpacing: '0.12em' }}>
        THIS REPLACES {count} RECORD{count === 1 ? '' : 'S'} ALREADY ON THE BOOKS
      </p>
      <ul className="mt-1.5 flex flex-col gap-1">
        {entries.map(([kind, ids]) => (
          <li key={kind} className="text-[11.5px]" style={{ color: 'rgba(244,244,245,0.85)' }}>
            <span className="label-mono text-[9.5px]" style={{ color: WARN }}>
              {kind.toUpperCase()} ({ids.length})
            </span>{' '}
            {ids.slice(0, OVERWRITE_IDS_SHOWN).join(', ')}
            {ids.length > OVERWRITE_IDS_SHOWN
              ? ` … and ${ids.length - OVERWRITE_IDS_SHOWN} more not listed here`
              : ''}
          </li>
        ))}
      </ul>
      <p className="mt-1.5 text-[11px]" style={{ color: 'rgba(244,244,245,0.7)' }}>
        Importing writes this file's values over those ids. A count somebody walked a shelf to
        verify is replaced by whatever the file says, with nothing left to show it was.
      </p>
    </div>
  );
}

function ProblemList({ problems }: { problems: ImportProblem[] }) {
  return (
    <div className="flex flex-col gap-2.5">
      {groupByKind(problems).map(({ kind, rows }) => {
        const shown = rows.slice(0, PROBLEMS_PER_KIND);
        const hidden = rows.length - shown.length;
        return (
          <div key={kind}>
            <p className="label-mono mb-1 text-[9.5px]" style={{ color: WARN, letterSpacing: '0.12em' }}>
              {kind.toUpperCase()} — {rows.length} PROBLEM{rows.length === 1 ? '' : 'S'}
            </p>
            <ul className="scroll-thin flex max-h-[260px] flex-col gap-1 overflow-y-auto">
              {shown.map((problem, index) => (
                <li
                  key={`${problem.kind}-${problem.row}-${problem.entityId}-${index}`}
                  data-testid="import-problem"
                  className="flex items-baseline gap-2.5 rounded-[9px] px-2.5 py-1.5"
                  style={{ background: 'rgba(255,255,255,0.04)' }}
                >
                  <span
                    className="label-mono tnum shrink-0 rounded-[5px] px-1.5 py-[2px] text-[9.5px] font-semibold"
                    style={{
                      color: DANGER,
                      background: `color-mix(in oklch, ${DANGER} 22%, transparent)`,
                    }}
                  >
                    {problem.row === null ? 'FILE' : `ROW ${problem.row}`}
                  </span>
                  <span className="text-[11.5px]" style={{ color: 'rgba(244,244,245,0.85)' }}>
                    {problem.describe}
                  </span>
                </li>
              ))}
            </ul>
            {hidden > 0 ? (
              <p className="mt-1 text-[11px]" style={{ color: WARN }}>
                {hidden} more {kind} problem{hidden === 1 ? '' : 's'} are not shown. Fix these and
                check again to see the rest.
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Report({ report }: { report: ImportReport }) {
  if (report.written) {
    const entries = nonEmpty(report.written);
    return (
      <div
        data-testid="import-result"
        className="flex flex-col gap-2 rounded-[13px] border px-[13px] py-[11px]"
        style={{
          borderColor: `color-mix(in oklch, ${OK} 50%, transparent)`,
          background: `color-mix(in oklch, ${OK} 10%, transparent)`,
        }}
      >
        <p className="label-mono text-[10px] font-semibold" style={{ color: OK, letterSpacing: '0.12em' }}>
          IMPORTED — THE CATALOG NOW HOLDS THIS
        </p>
        {entries.length === 0 ? (
          <p className="text-[11.5px]" style={{ color: MUTED }}>
            That file carried no rows, so nothing changed.
          </p>
        ) : (
          <CountRow label="WRITTEN" counts={report.written} />
        )}
      </div>
    );
  }

  if (!report.ok) {
    return (
      <div
        data-testid="import-result"
        className="flex flex-col gap-2.5 rounded-[13px] border px-[13px] py-[11px]"
        style={{
          borderColor: `color-mix(in oklch, ${DANGER} 55%, transparent)`,
          background: `color-mix(in oklch, ${DANGER} 11%, transparent)`,
        }}
      >
        <p className="label-mono text-[10px] font-semibold" style={{ color: DANGER, letterSpacing: '0.12em' }}>
          REFUSED — NOTHING WAS WRITTEN
        </p>
        {/* Stated rather than implied. The server validates the bundle whole and
            writes none of it if anything fails, so there is no half-imported
            catalog to go looking for — and the counts of what parsed are NOT
            shown here, because "412 parts" next to a refusal reads as a result. */}
        <p className="text-[11.5px]" style={{ color: 'rgba(244,244,245,0.82)' }}>
          {report.problems.length} problem{report.problems.length === 1 ? '' : 's'} in this file. The
          catalog is exactly as it was — not one row landed, including the rows that were fine. Fix
          these in the file and check it again.
        </p>
        <ProblemList problems={report.problems} />
      </div>
    );
  }

  return (
    <div
      data-testid="import-result"
      className="flex flex-col gap-2.5 rounded-[13px] border px-[13px] py-[11px]"
      style={{ borderColor: 'rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.04)' }}
    >
      <p className="label-mono text-[10px] font-semibold" style={{ color: OK, letterSpacing: '0.12em' }}>
        CHECKED CLEAN — NOTHING WRITTEN YET
      </p>
      <CountRow label="WOULD LAND" counts={report.counts} />
      <OverwriteWarning overwrites={report.overwrites} />
      <p className="text-[11px]" style={{ color: MUTED }}>
        This was a check. The catalog has not changed. IMPORT below writes it.
      </p>
    </div>
  );
}

export function SupplyImportPanel(): ReactNode {
  const { report, busy } = useSupplyImport();
  const [kind, setKind] = useState<ImportKind>('parts');
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  /**
   * The exact source a clean check was run against. The import button is keyed
   * off this rather than off the report, so editing one character after a green
   * check cannot import a file nobody checked.
   */
  const [checked, setChecked] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const signature = `${kind} ${text}`;
  const stale = checked !== null && checked !== signature;
  const canImport = checked === signature && !busy;

  const run = async (dryRun: boolean) => {
    setProblem(null);
    setNotice(null);
    const built = buildRequest(kind, text);
    if (built.error !== null) {
      setChecked(null);
      supplyActions.clearImport();
      setProblem(built.error);
      return;
    }

    const outcome = await supplyActions.runImport({ ...built.request, dryRun });
    if (outcome.error !== null) {
      // A specific reason from the server, shown as that reason. The check gate
      // is dropped: a 409 means the catalog moved, so the clean check that
      // enabled this button described data that no longer exists.
      setChecked(null);
      setProblem(outcome.error);
      return;
    }
    if (!dryRun) {
      // Written or refused, this source has had its one attempt. Re-enabling
      // import would need a fresh check against the catalog as it is now.
      setChecked(null);
      return;
    }
    const clean = outcome.report !== null && outcome.report.ok && total(outcome.report.counts) > 0;
    setChecked(clean ? signature : null);
  };

  const takeFile = async (file: File) => {
    setProblem(null);
    setNotice(null);
    setChecked(null);
    supplyActions.clearImport();
    setFileName(file.name);
    setText(await file.text());
  };

  const fetchTemplate = async () => {
    setProblem(null);
    setNotice(null);
    const result = await getImportTemplate(kind);
    if (!result.ok) {
      setProblem(result.error.message);
      return;
    }
    download(`supply-${kind}-template.csv`, 'text/csv', result.data.csv);
    setNotice(`Template for ${kind}: ${result.data.columns.join(', ')}`);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="label-mono text-[8.5px]" style={{ color: MUTED }}>
            WHAT IS IN THIS FILE
          </span>
          <select
            aria-label="Import kind"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as ImportKind);
              // The kind is half the meaning of a CSV. Changing it invalidates
              // the check for the same reason editing the text does.
              setChecked(null);
            }}
            className="well rounded-[8px] px-2.5 py-1.5 text-[12px] outline-none"
            style={{ color: WHITE }}
          >
            {IMPORT_KINDS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={() => void fetchTemplate()}
          className="rounded-[8px] border px-3.5 py-1.5 label-mono text-[9.5px]"
          style={{ borderColor: 'rgba(255,255,255,0.22)', color: WHITE }}
        >
          DOWNLOAD TEMPLATE
        </button>

        <input
          ref={inputRef}
          type="file"
          accept=".csv,.json,text/csv,application/json"
          className="hidden"
          data-testid="import-file-input"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void takeFile(file);
            event.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="rounded-[8px] border px-3.5 py-1.5 label-mono text-[9.5px]"
          style={{ borderColor: 'rgba(255,255,255,0.22)', color: WHITE }}
        >
          CHOOSE A FILE
        </button>

        {fileName ? (
          <span className="label-mono text-[9.5px]" style={{ color: MUTED }}>
            {fileName}
          </span>
        ) : null}
      </div>

      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files[0];
          if (file) void takeFile(file);
        }}
        className="rounded-[12px] border border-dashed p-1.5"
        style={{
          borderColor: dragging ? OK : 'rgba(255,255,255,0.2)',
          background: dragging ? 'rgba(255,255,255,0.06)' : 'transparent',
        }}
      >
        <textarea
          aria-label="CSV or JSON to import"
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setNotice(null);
          }}
          spellCheck={false}
          placeholder={`Drop a .csv or .json file here, or paste ${kind} rows…`}
          className="well scroll-thin h-[150px] w-full resize-y rounded-[9px] px-2.5 py-2 font-mono text-[11.5px] outline-none"
          style={{ color: WHITE }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void run(true)}
          disabled={busy}
          className="rounded-[8px] border px-4 py-1.5 label-mono text-[9.5px] font-semibold disabled:opacity-40"
          style={{
            borderColor: `color-mix(in oklch, ${OK} 55%, transparent)`,
            background: `color-mix(in oklch, ${OK} 14%, transparent)`,
            color: WHITE,
          }}
        >
          {busy ? 'CHECKING…' : 'CHECK THIS FILE'}
        </button>

        <button
          type="button"
          onClick={() => void run(false)}
          disabled={!canImport}
          title={canImport ? undefined : 'Check the file first — an unchecked import is not offered.'}
          className="rounded-[8px] border px-4 py-1.5 label-mono text-[9.5px] disabled:opacity-40"
          style={{ borderColor: 'rgba(255,255,255,0.22)', color: WHITE }}
        >
          IMPORT
        </button>

        <span className="text-[11px]" style={{ color: MUTED }}>
          {canImport
            ? 'Checked clean. Importing writes the whole file.'
            : 'Check the file first. Nothing is written by a check.'}
        </span>
      </div>

      {stale ? (
        <Note tone={WARN}>
          This file changed since it was checked, so the result below no longer describes it. Check
          it again before importing.
        </Note>
      ) : null}

      {problem ? <Note tone={DANGER}>{problem}</Note> : null}
      {notice ? <Note tone={MUTED}>{notice}</Note> : null}

      {report ? <Report report={report} /> : null}
    </div>
  );
}

export default SupplyImportPanel;
